import { useTranslations } from '@vocab/react';
import { Notice, Stack, Text, TextLinkButton } from 'braid-design-system';
import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { type Resume, VirusScanStatus } from 'src/__generated__/graphql';
import {
  ResumeFields,
  type ResumeFieldsValues,
} from 'src/components/ResumeFields/ResumeFields';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { useVirusScan } from 'src/hooks/useVirusScan';
import DrawerDocument from 'src/pages/Review/components/DrawerDocument.tsx';

import translations from './.vocab/index.ts';

interface Props {
  resume: Resume | undefined;
  resumes: Resume[];
  onChange: (values: Partial<ResumeFieldsValues>) => void;
}

export function ResumeItem({ resumes, resume, onChange }: Props) {
  const { t } = useTranslations(translations);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const rootForm = useFormContext<ResumeFieldsValues>();
  const form = useForm<ResumeFieldsValues>();
  const { handleSubmit, reset, formState } = form;

  useVirusScan({
    fileUri: rootForm.watch('resume-uri'),
    currentMethod: rootForm.watch('resume-method'),
    onStatusChange: (virusScanStatus, method) => {
      const shouldCheck =
        method === FileAttachment.UPLOAD || method === FileAttachment.CHANGE;

      if (!shouldCheck) {
        rootForm.clearErrors('resume-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        rootForm.setError('resume-virusScan', {
          type: VirusScanStatus.HasVirus,
          message: t('error.virus.resume', {
            Button: (text) => text,
          })?.toString(),
        });
      } else if (virusScanStatus === VirusScanStatus.Pending) {
        rootForm.setError('resume-virusScan', {
          type: VirusScanStatus.Pending,
          message: t('error.virus.pending'),
        });
      } else {
        rootForm.clearErrors('resume-virusScan');
      }
    },
  });

  const closeForm = () => {
    setIsFormOpen(false);
    reset(rootForm.getValues());
  };

  const confirmEdit = (values: Partial<ResumeFieldsValues>) => {
    onChange(values);
    setIsFormOpen(false);
  };

  const virusScanStatus = rootForm.formState.errors['resume-virusScan']?.type;

  return (
    <Stack space="small">
      <DrawerDocument
        title={t('Resume')}
        currentSelectionText={
          resume?.fileMetadata.name || t('No resume included')
        }
        currentSelectionTestId={`resume-${resume ? 'upload' : 'dont'}`}
        editButtonLabel={t('Edit resume')}
        editButtonId="edit-resume"
        onSubmit={handleSubmit(confirmEdit)}
        onClose={closeForm}
        shouldConfirmBeforeClose={formState.isDirty}
        isFormOpen={isFormOpen}
        setIsFormOpen={setIsFormOpen}
      >
        <FormProvider {...form}>
          <ResumeFields
            hideTitle
            resumes={resumes}
            defaultMethod={resume ? FileAttachment.CHANGE : FileAttachment.NONE}
            defaultValue={resume}
          />
        </FormProvider>
      </DrawerDocument>
      {virusScanStatus === VirusScanStatus.HasVirus && (
        <Notice tone="critical">
          <Text size="small">
            {t('error.virus.resume', {
              Button: (text) => (
                <TextLinkButton onClick={() => setIsFormOpen(true)}>
                  {text}
                </TextLinkButton>
              ),
            })}
          </Text>
        </Notice>
      )}
      {virusScanStatus === VirusScanStatus.Pending && (
        <Notice tone="critical">
          <Text size="small">{t('error.virus.pending')}</Text>
        </Notice>
      )}
    </Stack>
  );
}
